'use client';
import { AUTOMATIONS_ICON } from '#pplx/frontend/apps/ask/automations-shared/constants.js';
import { usePluginEntitiesEnabled, usePluginsNavigationHrefs, } from '#pplx/frontend/apps/ask/connectors/hooks/usePluginsNavigationHrefs.js';
import { useCachedSessionsPageExperimentVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useCachedSessionsPageExperimentVariation.js';
import { useComputerMemoryPageVisible } from '#pplx/frontend/apps/ask/hooks/computer-hooks/useComputerMemoryPageVisible.js';
import { COMPUTER_TASKS_HREF, isComputerTaskThreadRoute, } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useMemo } from 'react';
import { defineMessage } from 'react-intl';
const CONNECTORS_HREF = '/computer/connectors';
const SKILLS_HREF = '/computer/skills';
const MEMORY_HREF = '/computer/memory';
const UTILITY_HREFS = [CONNECTORS_HREF, SKILLS_HREF, MEMORY_HREF];
const PLUGINS_HREF = '/computer/plugins';
// The dedicated Sessions variation shows this in place of the Computer item.
export const SESSIONS_HREF = '/library';
const SESSIONS_NAV_ITEM = {
    text: defineMessage({
        defaultMessage: 'Sessions',
        id: '1d5175c0c7',
        description: 'Page title shown on the library page',
    }),
    testId: 'sessions',
    icon: $icon('history'),
    href: SESSIONS_HREF,
};
const NEW_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'New',
    description: 'Tab label for the Perplexity search home in the desktop sidebar navigation',
    id: 'ecb72addb8',
});
const COMPUTER_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Computer',
    description: 'Tab label for the Computer section in the desktop sidebar navigation',
    id: 'b5f87829ae',
});
const AUTOMATIONS_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Automations',
    description: 'Tab label for the Automations section in the desktop sidebar navigation',
    id: 'b306759596',
});
const ARTIFACTS_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Artifacts',
    description: 'Tab label for the Artifacts section in the desktop sidebar navigation',
    id: '333d6a3b8a',
});
const PLUGINS_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Plugins',
    id: '3824f53485',
    description: 'Tab label for the Plugins section in the desktop sidebar navigation',
});
const CUSTOMIZE_SECTION_MESSAGE = defineMessage({
    defaultMessage: 'Customize',
    description: 'Section heading for the more section in the home sidebar',
    id: 'b303ffa3c2',
});
export const AUTOMATIONS_HREF = '/computer/automations';
function isUtilityRoute(pathname, href) {
    return pathname === href || (pathname?.startsWith(`${href}/`) ?? false);
}
export function useSidebarNavigationItems({ newThreadShortcutLabel, onAutomationsDashboardHover, onComputerDashboardHover, }) {
    const { connectors } = usePluginsNavigationHrefs();
    const variation = useCachedSessionsPageExperimentVariation({ defaultValue: 'control' });
    const { visible: isComputerMemoryEnabled } = useComputerMemoryPageVisible();
    const { enabled: showPlugins } = usePluginEntitiesEnabled();
    const filteredUtilityHrefs = useMemo(() => UTILITY_HREFS.filter((href) => {
        if (href === MEMORY_HREF) {
            return isComputerMemoryEnabled;
        }
        return true;
    }), [isComputerMemoryEnabled]);
    const sections = useMemo(() => {
        return [
            {
                text: NEW_SECTION_MESSAGE,
                testId: 'unified_home',
                icon: $icon('custom-plus-large'),
                href: '/',
                collapseSettingsKey: 'isSidebarAppsCollapsed',
                shortcut: newThreadShortcutLabel,
                foreground: true,
                selected: false,
            },
            // The consolidated variation folds Computer into the Sessions page without a nav item.
            ...(variation === 'consolidated'
                ? []
                : [
                    {
                        ...(variation === 'dedicated'
                            ? SESSIONS_NAV_ITEM
                            : {
                                text: COMPUTER_SECTION_MESSAGE,
                                testId: 'computer_tasks',
                                icon: $icon('custom-computer'),
                                href: COMPUTER_TASKS_HREF,
                                isChildRouteSelected: isComputerTaskThreadRoute,
                                onPointerEnter: onComputerDashboardHover,
                            }),
                        collapseSettingsKey: 'isSidebarUtilitiesCollapsed',
                        defaultExpanded: false,
                        foreground: true,
                    },
                ]),
            {
                text: AUTOMATIONS_SECTION_MESSAGE,
                testId: 'computer_automations',
                icon: AUTOMATIONS_ICON,
                href: AUTOMATIONS_HREF,
                collapseSettingsKey: 'isSidebarAppsCollapsed',
                isChildRouteSelected: (pathname) => pathname === AUTOMATIONS_HREF || (pathname?.startsWith(`${AUTOMATIONS_HREF}/`) ?? false),
                onPointerEnter: onAutomationsDashboardHover,
                foreground: true,
            },
            {
                text: ARTIFACTS_SECTION_MESSAGE,
                testId: 'computer_artifacts',
                icon: $icon('layout-collage'),
                href: '/computer/artifacts',
                collapseSettingsKey: 'isSidebarArtifactsCollapsed',
                defaultExpanded: false,
                foreground: true,
            },
            showPlugins
                ? {
                    text: PLUGINS_SECTION_MESSAGE,
                    testId: 'computer_plugins',
                    icon: $icon('plug'),
                    href: PLUGINS_HREF,
                    collapseSettingsKey: 'isSidebarAppsCollapsed',
                    isChildRouteSelected: (pathname) => isUtilityRoute(pathname, PLUGINS_HREF),
                    meta: true,
                    defaultExpanded: true,
                    foreground: true,
                }
                : {
                    text: CUSTOMIZE_SECTION_MESSAGE,
                    testId: 'more-button',
                    icon: $icon('settings-2'),
                    href: connectors,
                    collapseSettingsKey: 'isSidebarAppsCollapsed',
                    isChildRouteSelected: (pathname) => filteredUtilityHrefs.some((href) => isUtilityRoute(pathname, href)) ||
                        isUtilityRoute(pathname, connectors),
                    meta: true,
                    defaultExpanded: true,
                    foreground: true,
                },
        ];
    }, [
        showPlugins,
        connectors,
        filteredUtilityHrefs,
        newThreadShortcutLabel,
        onAutomationsDashboardHover,
        onComputerDashboardHover,
        variation,
    ]);
    return sections;
}
//# sourceMappingURL=useSidebarNavigationItems.js.map